import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
// Experience type based on schema
interface Experience {
  id: string;
  customerId: string | null;
  type: string;
  date: string;
  location: string | null;
  participants: number | null;
  status: string | null;
  notes: string | null;
  createdAt: Date | null;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  totalAmount?: number | null;
}
import { Calendar, Check, Clock, Users, MapPin, CalendarClock } from "lucide-react";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  confirmed: "bg-green-100 text-green-800 border-green-300",
  completed: "bg-blue-100 text-blue-800 border-blue-300",
  cancelled: "bg-red-100 text-red-800 border-red-300",
  scheduled: "bg-purple-100 text-purple-800 border-purple-300",
};

const experienceTypes: Record<string, string> = {
  workshop: "Workshop",
  private_lesson: "Private Lesson",
  corporate_event: "Corporate Event",
};

function formatDate(date: Date | string | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export function ExperiencesList() {
  const { toast } = useToast();
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);
  const [newDate, setNewDate] = useState("");

  const { data: experiences, isLoading } = useQuery<Experience[]>({
    queryKey: ["/api/admin/experiences"],
  });

  const confirmMutation = useMutation({
    mutationFn: async (experienceId: string) => {
      return apiRequest("POST", `/api/admin/experiences/${experienceId}/confirm`);
    },
    onSuccess: () => {
      toast({ title: "Experience confirmed", description: "Confirmation email sent to customer." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/experiences"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: async ({ id, date }: { id: string; date: string }) => {
      return apiRequest("POST", `/api/admin/experiences/${id}/reschedule`, { newDate: date });
    },
    onSuccess: () => {
      toast({ title: "Experience rescheduled", description: "Customer has been notified." });
      setRescheduleId(null);
      setNewDate("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/experiences"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Group experiences by date for calendar view
  const groupedExperiences = experiences?.reduce((acc, exp) => {
    const dateKey = exp.date;
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(exp);
    return acc;
  }, {} as Record<string, Experience[]>);

  // Sort dates
  const sortedDates = Object.keys(groupedExperiences || {}).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  // Filter for upcoming
  const today = new Date().toISOString().split("T")[0];
  const upcomingDates = sortedDates.filter((date) => date >= today);

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48 mb-6" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Calendar View */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Upcoming Experiences</h3>
        </div>

        {upcomingDates.length === 0 ? (
          <div className="text-center py-12">
            <CalendarClock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No upcoming experiences scheduled</p>
          </div>
        ) : (
          <div className="space-y-6">
            {upcomingDates.map((date) => (
              <div key={date} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold">{formatDate(date)}</span>
                  <Badge variant="secondary">
                    {groupedExperiences![date].length}{" "}
                    {groupedExperiences![date].length === 1 ? "booking" : "bookings"}
                  </Badge>
                </div>

                <div className="space-y-3">
                  {groupedExperiences![date].map((exp) => (
                    <div
                      key={exp.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {experienceTypes[exp.type] || exp.type}
                          </span>
                          <Badge
                            variant="outline"
                            className={statusColors[exp.status || "pending"]}
                          >
                            {exp.status || "pending"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {exp.participants} participants
                          </span>
                          {exp.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {exp.location}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {exp.status === "pending" || exp.status === "scheduled" ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => confirmMutation.mutate(exp.id)}
                              disabled={confirmMutation.isPending}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Confirm
                            </Button>
                            <Dialog
                              open={rescheduleId === exp.id}
                              onOpenChange={(open) => {
                                if (!open) {
                                  setRescheduleId(null);
                                  setNewDate("");
                                }
                              }}
                            >
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setRescheduleId(exp.id)}
                                >
                                  <Calendar className="h-4 w-4 mr-1" />
                                  Reschedule
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Reschedule Experience</DialogTitle>
                                  <DialogDescription>
                                    Select a new date for this {experienceTypes[exp.type] || exp.type}
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="py-4">
                                  <Input
                                    type="date"
                                    value={newDate}
                                    onChange={(e) => setNewDate(e.target.value)}
                                    min={today}
                                  />
                                </div>
                                <DialogFooter>
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      setRescheduleId(null);
                                      setNewDate("");
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={() =>
                                      rescheduleMutation.mutate({ id: exp.id, date: newDate })
                                    }
                                    disabled={!newDate || rescheduleMutation.isPending}
                                  >
                                    Reschedule
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {formatCurrency(exp.totalAmount)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* All Experiences Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-6">All Bookings</h3>

        {!experiences || experiences.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No experience bookings found</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Participants</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {experiences.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell className="font-medium">
                      {experienceTypes[exp.type] || exp.type}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{exp.customerName}</p>
                        <p className="text-sm text-muted-foreground">{exp.customerEmail}</p>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(exp.date)}</TableCell>
                    <TableCell>{exp.location || "-"}</TableCell>
                    <TableCell>{exp.participants}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={statusColors[exp.status || "pending"]}
                      >
                        {exp.status || "pending"}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatCurrency(exp.totalAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
